// Maps an execution's raw status onto what the table shows for it: tag colour,
// label, icon, and — where the backend sent one — the first error to surface.

import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';
import CheckCircle from '@decisiv/iconix/lib/components/CheckCircle';
import ExclamationTriangle from '@decisiv/iconix/lib/components/ExclamationTriangle';
import ExclamationTriangleF from '@decisiv/iconix/lib/components/ExclamationTriangleF';
import ExclamationShield from '@decisiv/iconix/lib/components/ExclamationShield';
import Hourglass from '@decisiv/iconix/lib/components/Hourglass';
import isEmpty from 'lodash/isEmpty';

const MAX_DETAIL_LENGTH = 100;

export function truncateText(str, maxLength = MAX_DETAIL_LENGTH) {
  if (isEmpty(str)) return '';
  if (str.length > maxLength) return `${str.substring(0, maxLength)}...`;
  return str;
}

const firstError = (attributes) => attributes.error_summary?.errors?.[0];

export default function describeStatus(attributes) {
  const base = { errorTitle: undefined, errorDesc: undefined };

  switch (attributes.status) {
    case 'SUCCEEDED':
      return {
        ...base,
        color: 'success',
        text: 'Succeeded',
        icon: CheckCircleF,
      };
    case 'ExecutionDoesNotExist':
      return {
        ...base,
        color: 'danger',
        text: 'Execution Does Not Exist',
        icon: ExclamationTriangleF,
      };
    case 'RUNNING':
      return {
        ...base,
        color: 'information',
        text: 'Processing',
        icon: Hourglass,
      };
    case 'NOT_FOUND': {
      // A single error here is the "no results" marker itself, so only a
      // second one means something went wrong worth showing.
      const errors = attributes.error_summary?.errors;
      const hasExtra = errors && errors.length > 1;

      return {
        color: 'success',
        text: 'Succeeded - No Results',
        icon: CheckCircle,
        errorTitle: hasExtra ? errors[0].title : null,
        errorDesc: hasExtra ? truncateText(errors[0].detail) : null,
      };
    }
    case 'BLOCKED_BY_CIRCUIT_BREAKER':
      return {
        ...base,
        color: 'warning',
        text: 'Blocked By Circuit Breaker',
        icon: ExclamationShield,
      };
    case 'RE_ENQUEUED_BY_CIRCUIT_BREAKER':
      return {
        ...base,
        color: 'warning',
        text: 'Re Enqueued By Circuit Breaker',
        icon: ExclamationShield,
      };
    case 'BLOCKED_BY_CONDITION_RULE':
      return {
        ...base,
        color: 'information',
        text: 'Blocked By Condition',
        icon: ExclamationShield,
      };
    case 'COMPLETED_WITH_ERRORS':
      return {
        ...base,
        color: 'warning',
        text: 'Completed with Errors',
        icon: ExclamationTriangle,
      };
    case 'FAILED':
    default:
      return {
        color: 'danger',
        text: 'Failed',
        icon: ExclamationTriangle,
        errorTitle: firstError(attributes)?.title,
        errorDesc: truncateText(firstError(attributes)?.detail),
      };
  }
}
